/**
 * SignaturePadModal — shared draw-your-signature dialog (mouse / touch).
 *
 * Used by the forms editor (inserts the therapist signature into a form) and
 * by the AI treatments summary export (saves a reusable therapist signature).
 * Emits a PNG data URL, normalized to a bounded width so stored signatures
 * stay small (~20–60 KB).
 *
 * 2026 shell: centered card on desktop, bottom sheet on mobile.
 */

import { useEffect, useRef, useState } from "react";
import { Eraser, PencilLine } from "@phosphor-icons/react";
import ModalCloseButton from "./ModalCloseButton";
import "./SignaturePadModal.css";

const CANVAS_HEIGHT = 180;
const EXPORT_MAX_WIDTH = 640;

function SignaturePadModal({
  title = "חתימת מטפל",
  hint = "חתמו באמצעות העכבר או מסך מגע.",
  confirmLabel = "אישור",
  ConfirmIcon = PencilLine,
  onConfirm,
  onClose,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
    setHasDrawn(true);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    if (e.cancelable) e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stop = () => {
    drawingRef.current = false;
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  /** Export at a bounded width so the PNG stays small regardless of screen. */
  const exportDataUrl = () => {
    const source = canvasRef.current;
    if (source.width <= EXPORT_MAX_WIDTH) return source.toDataURL("image/png");
    const scale = EXPORT_MAX_WIDTH / source.width;
    const out = document.createElement("canvas");
    out.width = EXPORT_MAX_WIDTH;
    out.height = Math.round(source.height * scale);
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(source, 0, 0, out.width, out.height);
    return out.toDataURL("image/png");
  };

  const confirm = () => {
    if (!hasDrawn) return;
    onConfirm?.(exportDataUrl());
  };

  return (
    <div className="spm-overlay" onClick={onClose}>
      <div
        className="spm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="spm-header">
          <div className="spm-header-text">
            <span className="spm-icon">
              <PencilLine size={18} weight="fill" />
            </span>
            <div>
              <span className="spm-eyebrow">חתימה</span>
              <h3 className="spm-title">{title}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div className="spm-body">
          <p className="spm-hint">{hint}</p>
          <div className="spm-canvas-wrap">
            <canvas
              ref={canvasRef}
              className="spm-canvas"
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={stop}
              onMouseLeave={stop}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={stop}
            />
          </div>
        </div>
        <div className="spm-footer">
          <button type="button" className="spm-btn secondary" onClick={clear}>
            <Eraser size={16} />
            <span>נקה</span>
          </button>
          <button
            type="button"
            className="spm-btn primary"
            onClick={confirm}
            disabled={!hasDrawn}
          >
            <ConfirmIcon size={16} weight="bold" />
            <span>{confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignaturePadModal;
